import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Checkbox,
  Chip,
  Collapse,
  Container,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Link,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
  Flag as FlagIcon,
  KeyboardArrowDown as ExpandMoreIcon,
  KeyboardArrowUp as ExpandLessIcon,
  OpenInNew as OpenInNewIcon,
  Refresh as RefreshIcon,
  Science as ScienceIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  LocalHospital as DiseaseIcon,
  Slideshow as SlideshowIcon,
} from '@mui/icons-material';
import {
  EntitySearchResult,
  getCompanyPresentations,
  getCompanyProducts,
  getCoveragePresentations,
  getEntityMilestones,
  getMilestoneOverview,
  getMilestoneStatsByDimension,
  searchEntities,
} from '../../api/milestones';
import {
  CompanyPresentation,
  CoveragePresentation,
  MilestoneDetail,
  MilestoneDashboardOverview,
  MilestoneDimension,
  MilestoneDimensionRow,
  MilestoneDimensionStats,
  MilestoneEntityDimension,
} from '../../types/milestone';
import LoadingSpinner from '../../components/common/LoadingSpinner/LoadingSpinner';
import ErrorMessage from '../../components/common/ErrorMessage/ErrorMessage';
import { useEnvironment } from '../../hooks/useEnvironment';

// --- Small presentational helpers ---------------------------------------

interface StatCardProps {
  label: string;
  value: number;
  icon: React.ReactNode;
  color?: string;
  hint?: string;
  // When provided, the card becomes clickable (used for the coverage drill-down).
  onClick?: () => void;
  selected?: boolean;
}

const StatCard: React.FC<StatCardProps> = ({
  label,
  value,
  icon,
  color,
  hint,
  onClick,
  selected,
}) => {
  const inner = (
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Box sx={{ color: color || 'primary.main', display: 'flex' }}>{icon}</Box>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
        {hint && (
          <Tooltip title={hint}>
            <Box component="span" sx={{ color: 'text.disabled', fontSize: 12 }}>
              &#9432;
            </Box>
          </Tooltip>
        )}
      </Box>
      <Typography variant="h4" sx={{ fontWeight: 700, color: color || 'text.primary' }}>
        {value.toLocaleString()}
      </Typography>
      {onClick && (
        <Typography variant="caption" color="primary" sx={{ display: 'block', mt: 0.5 }}>
          {selected ? 'Hide presentations' : 'View presentations'}
        </Typography>
      )}
    </CardContent>
  );

  return (
    <Card
      sx={{
        flex: '1 1 160px',
        minWidth: 160,
        outline: selected ? '2px solid' : 'none',
        outlineColor: 'primary.main',
      }}
    >
      {onClick ? (
        <CardActionArea onClick={onClick} sx={{ height: '100%' }}>
          {inner}
        </CardActionArea>
      ) : (
        inner
      )}
    </Card>
  );
};

const DIMENSION_LABELS: Record<MilestoneDimension, string> = {
  company: 'Company',
  technology: 'Technology',
  disease: 'Disease',
};

const STATUS_COLORS: Record<string, string> = {
  active: '#1565c0',
  completed: '#455a64',
  canceled: '#757575',
};

// Which source document a milestone came from. Deck and press-release
// milestones coexist for the same company (they are extracted and diffed
// independently), so the source has to be visible to read the list correctly.
const SOURCE_LABELS: Record<string, string> = {
  presentation: 'Deck',
  press_release: 'Press release',
};

const SOURCE_COLORS: Record<string, string> = {
  presentation: '#6a1b9a',
  press_release: '#00695c',
};

/** Milestones written before source_type existed are deck-derived. */
const sourceTypeOf = (m: { source_type?: string | null }): string =>
  m.source_type || 'presentation';

// SOZO Intuition webapp base for deep-linking to a company page.
const SOZO_WEBAPP_URL =
  process.env.REACT_APP_WEBAPP_API_URL || 'https://sozointuition.com';
const sozoCompanyUrl = (companyId: string) =>
  `${SOZO_WEBAPP_URL.replace(/\/$/, '')}/company/${encodeURIComponent(companyId)}`;

// date_created is an ISO timestamp; show just the date part.
const formatCreated = (iso: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString();
};

// date_start/date_end are ISO date strings (or partials); pair them into a range.
const formatRange = (start: string | null, end: string | null): string => {
  if (start && end) return start === end ? start : `${start} → ${end}`;
  return start || end || '—';
};

// --- Expandable breakdown row -------------------------------------------

interface MilestoneRowProps {
  row: MilestoneDimensionRow;
  dimension: MilestoneEntityDimension;
  days?: number;
  // A token that changes whenever a global refresh happens or filters change,
  // so an already-expanded row knows to refetch.
  refreshToken: number;
}

const MilestoneBreakdownRow: React.FC<MilestoneRowProps> = ({
  row,
  dimension,
  days,
  refreshToken,
}) => {
  const [open, setOpen] = useState(false);
  const [milestones, setMilestones] = useState<MilestoneDetail[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);

  // Secondary expansion (company dimension only): the company's presentations,
  // including decks that yielded no milestones — for diagnosing coverage.
  const [presOpen, setPresOpen] = useState(false);
  const [presentations, setPresentations] = useState<
    CompanyPresentation[] | null
  >(null);
  const [presLoading, setPresLoading] = useState(false);
  const [presError, setPresError] = useState(false);
  const canViewPresentations = dimension === 'company';
  // 'all' | 'presentation' | 'press_release'. Client-side: the row's milestones
  // are already loaded, so filtering here avoids a refetch per toggle.
  const [sourceFilter, setSourceFilter] = useState<string>('all');

  // For technology/disease, milestones belong to a therapeutic developed by a
  // company; surface both. For the company dimension the row IS the company,
  // and for the product dimension every milestone shares the same therapeutic —
  // so those columns are redundant and hidden.
  const showEntity = dimension !== 'company' && dimension !== 'product';

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const data = await getEntityMilestones(dimension, row.id, days);
      setMilestones(data.milestones);
    } catch (e) {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [dimension, row.id, days]);

  // Fetch when first opened; refetch an open row when filters/refresh change.
  useEffect(() => {
    if (open) {
      load();
    } else {
      // Drop stale data so a collapsed row reloads fresh next open.
      setMilestones(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, load, refreshToken]);

  const loadPresentations = useCallback(async () => {
    setPresLoading(true);
    setPresError(false);
    try {
      const data = await getCompanyPresentations(row.id);
      setPresentations(data.presentations);
    } catch (e) {
      setPresError(true);
    } finally {
      setPresLoading(false);
    }
  }, [row.id]);

  useEffect(() => {
    if (presOpen) {
      loadPresentations();
    } else {
      setPresentations(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presOpen, loadPresentations, refreshToken]);

  return (
    <>
      <TableRow hover sx={{ '& > *': { borderBottom: 'unset' } }}>
        <TableCell sx={{ width: 48 }}>
          <IconButton
            size="small"
            onClick={() => setOpen((o) => !o)}
            aria-label={open ? 'collapse' : 'expand'}
          >
            {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <span>{row.name || row.id}</span>
            {canViewPresentations && (
              <Button
                size="small"
                variant="text"
                startIcon={<SlideshowIcon fontSize="small" />}
                onClick={() => setPresOpen((o) => !o)}
                sx={{ textTransform: 'none', py: 0 }}
              >
                {presOpen ? 'Hide presentations' : 'Presentations'}
              </Button>
            )}
          </Box>
        </TableCell>
        <TableCell align="right">{row.active}</TableCell>
        <TableCell align="right">{row.completed}</TableCell>
        <TableCell align="right">{row.canceled}</TableCell>
        <TableCell align="right" sx={{ fontWeight: 600 }}>
          {row.total}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell sx={{ py: 0 }} colSpan={6}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ my: 2, ml: 2 }}>
              {loading ? (
                <LoadingSpinner message="Loading milestones..." />
              ) : loadError ? (
                <Typography variant="body2" color="error">
                  Failed to load milestones for {row.name || row.id}.
                </Typography>
              ) : !milestones || milestones.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No milestones to show.
                </Typography>
              ) : (
                <>
                <Box
                  sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <Typography variant="caption" color="text.secondary">
                    Source:
                  </Typography>
                  {(['all', 'presentation', 'press_release'] as const).map(
                    (key) => {
                      const count =
                        key === 'all'
                          ? milestones.length
                          : milestones.filter((m) => sourceTypeOf(m) === key)
                              .length;
                      return (
                        <Chip
                          key={key}
                          label={`${
                            key === 'all' ? 'All' : SOURCE_LABELS[key]
                          } (${count})`}
                          size="small"
                          variant={sourceFilter === key ? 'filled' : 'outlined'}
                          onClick={() => setSourceFilter(key)}
                          sx={{
                            cursor: 'pointer',
                            ...(sourceFilter === key && key !== 'all'
                              ? {
                                  bgcolor: SOURCE_COLORS[key],
                                  color: 'white',
                                }
                              : {}),
                          }}
                        />
                      );
                    }
                  )}
                </Box>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Milestone</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Source</TableCell>
                      <TableCell>Created</TableCell>
                      <TableCell>Target dates</TableCell>
                      {showEntity && <TableCell>Therapeutic</TableCell>}
                      {showEntity && <TableCell>Company</TableCell>}
                      <TableCell>Document</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {milestones
                      .filter(
                        (m) =>
                          sourceFilter === 'all' ||
                          sourceTypeOf(m) === sourceFilter
                      )
                      .map((m) => (
                      <TableRow key={m.id} hover>
                        <TableCell sx={{ maxWidth: 360 }}>{m.title}</TableCell>
                        <TableCell>
                          <Chip
                            label={m.status}
                            size="small"
                            sx={{
                              bgcolor: STATUS_COLORS[m.status] || '#9e9e9e',
                              color: 'white',
                              textTransform: 'capitalize',
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={
                              SOURCE_LABELS[sourceTypeOf(m)] || sourceTypeOf(m)
                            }
                            size="small"
                            variant="outlined"
                            sx={{
                              borderColor:
                                SOURCE_COLORS[sourceTypeOf(m)] || '#9e9e9e',
                              color:
                                SOURCE_COLORS[sourceTypeOf(m)] || '#9e9e9e',
                            }}
                          />
                        </TableCell>
                        <TableCell>{formatCreated(m.date_created)}</TableCell>
                        <TableCell>
                          {formatRange(m.date_start, m.date_end)}
                          {m.date_label ? (
                            <Typography
                              component="span"
                              variant="caption"
                              color="text.secondary"
                              sx={{ ml: 0.5 }}
                            >
                              ({m.date_label})
                            </Typography>
                          ) : null}
                        </TableCell>
                        {showEntity && (
                          <TableCell sx={{ maxWidth: 200 }}>
                            {m.product_name || '—'}
                          </TableCell>
                        )}
                        {showEntity && (
                          <TableCell sx={{ maxWidth: 200 }}>
                            {m.company_id && m.company_name ? (
                              <Link
                                href={sozoCompanyUrl(m.company_id)}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                }}
                              >
                                {m.company_name}
                                <OpenInNewIcon sx={{ fontSize: 14 }} />
                              </Link>
                            ) : (
                              m.company_name || '—'
                            )}
                          </TableCell>
                        )}
                        <TableCell sx={{ maxWidth: 280 }}>
                          {m.presentation_url ? (
                            <Link
                              href={m.presentation_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.5,
                              }}
                            >
                              {m.presentation_title ||
                                `View ${
                                  SOURCE_LABELS[
                                    sourceTypeOf(m)
                                  ]?.toLowerCase() || 'document'
                                }`}
                              <OpenInNewIcon sx={{ fontSize: 14 }} />
                            </Link>
                          ) : (
                            m.presentation_title || '—'
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                </>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>

      {/* Secondary panel: the company's presentations (incl. zero-milestone). */}
      {canViewPresentations && (
        <TableRow>
          <TableCell sx={{ py: 0 }} colSpan={6}>
            <Collapse in={presOpen} timeout="auto" unmountOnExit>
              <Box sx={{ my: 2, ml: 2 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mb: 1 }}
                >
                  All presentations for {row.name || row.id} — including decks
                  that produced no milestones (newest first).
                </Typography>
                {presLoading ? (
                  <LoadingSpinner message="Loading presentations..." />
                ) : presError ? (
                  <Typography variant="body2" color="error">
                    Failed to load presentations.
                  </Typography>
                ) : !presentations || presentations.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No presentations found.
                  </Typography>
                ) : (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Published</TableCell>
                        <TableCell>Presentation</TableCell>
                        <TableCell align="right">Active</TableCell>
                        <TableCell align="right">Completed</TableCell>
                        <TableCell align="right">Cancelled</TableCell>
                        <TableCell align="right">Milestones</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {presentations.map((p) => (
                        <TableRow
                          key={p.presentation_id}
                          hover
                          // Subtly flag decks with no milestones — the "why".
                          sx={{
                            bgcolor:
                              p.total === 0 ? 'action.hover' : 'inherit',
                          }}
                        >
                          <TableCell>{formatCreated(p.published_date)}</TableCell>
                          <TableCell sx={{ maxWidth: 360 }}>
                            {p.presentation_url ? (
                              <Link
                                href={p.presentation_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                }}
                              >
                                {p.presentation_title || 'View presentation'}
                                <OpenInNewIcon sx={{ fontSize: 14 }} />
                              </Link>
                            ) : (
                              p.presentation_title || '—'
                            )}
                          </TableCell>
                          <TableCell align="right">{p.active}</TableCell>
                          <TableCell align="right">{p.completed}</TableCell>
                          <TableCell align="right">{p.canceled}</TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              fontWeight: 600,
                              color:
                                p.total === 0 ? 'text.disabled' : 'text.primary',
                            }}
                          >
                            {p.total}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

// --- Coverage drill-down: paginated presentation list -------------------

const COVERAGE_PAGE_SIZE = 10;

interface CoverageDrilldownProps {
  hasMilestones: boolean;
  days?: number;
  // Bumped on manual refresh so an open drill-down refetches its page.
  refreshToken: number;
}

const CoverageDrilldown: React.FC<CoverageDrilldownProps> = ({
  hasMilestones,
  days,
  refreshToken,
}) => {
  const [items, setItems] = useState<CoveragePresentation[]>([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);

  const load = useCallback(
    async (nextSkip: number) => {
      setLoading(true);
      setLoadError(false);
      try {
        const page = await getCoveragePresentations(hasMilestones, {
          days,
          skip: nextSkip,
          limit: COVERAGE_PAGE_SIZE,
        });
        setItems(page.items);
        setTotal(page.total);
        setSkip(page.skip);
      } catch (e) {
        setLoadError(true);
      } finally {
        setLoading(false);
      }
    },
    [hasMilestones, days],
  );

  // Reset to the first page whenever the filter, box, or refresh changes.
  useEffect(() => {
    load(0);
  }, [load, refreshToken]);

  const from = total === 0 ? 0 : skip + 1;
  const to = Math.min(skip + items.length, total);
  const canPrev = skip > 0;
  const canNext = skip + COVERAGE_PAGE_SIZE < total;

  return (
    <Box sx={{ mb: 4, mt: -2 }}>
      <Paper variant="outlined">
        {loadError ? (
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" color="error">
              Failed to load presentations.
            </Typography>
          </Box>
        ) : loading && items.length === 0 ? (
          <Box sx={{ p: 3 }}>
            <LoadingSpinner message="Loading presentations..." />
          </Box>
        ) : (
          <>
            <TableContainer sx={{ maxHeight: 420 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Company</TableCell>
                    <TableCell>Presentation</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={2} align="center" sx={{ py: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                          No presentations to show.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    items.map((p) => (
                      <TableRow key={p.presentation_id} hover>
                        <TableCell sx={{ maxWidth: 320 }}>
                          {p.company_id && p.company_name ? (
                            <Link
                              href={sozoCompanyUrl(p.company_id)}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.5,
                              }}
                            >
                              {p.company_name}
                              <OpenInNewIcon sx={{ fontSize: 14 }} />
                            </Link>
                          ) : (
                            p.company_name || '—'
                          )}
                        </TableCell>
                        <TableCell sx={{ maxWidth: 420 }}>
                          {p.presentation_url ? (
                            <Link
                              href={p.presentation_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.5,
                              }}
                            >
                              {p.presentation_title || 'View presentation'}
                              <OpenInNewIcon sx={{ fontSize: 14 }} />
                            </Link>
                          ) : (
                            p.presentation_title || '—'
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: 2,
                p: 1,
                borderTop: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {from.toLocaleString()}–{to.toLocaleString()} of{' '}
                {total.toLocaleString()}
              </Typography>
              <Button
                size="small"
                disabled={!canPrev || loading}
                onClick={() => load(Math.max(0, skip - COVERAGE_PAGE_SIZE))}
              >
                Prev
              </Button>
              <Button
                size="small"
                disabled={!canNext || loading}
                onClick={() => load(skip + COVERAGE_PAGE_SIZE)}
              >
                Next
              </Button>
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
};

const MilestoneDashboardPage: React.FC = () => {
  const { environment } = useEnvironment();

  const [overview, setOverview] = useState<MilestoneDashboardOverview | null>(null);
  const [dimension, setDimension] = useState<MilestoneDimension>('company');
  const [dimensionStats, setDimensionStats] =
    useState<MilestoneDimensionStats | null>(null);

  // Days filter for the breakdown table. Empty string = no filter.
  const [daysInput, setDaysInput] = useState<string>('');
  const [appliedDays, setAppliedDays] = useState<number | undefined>(undefined);

  const [loadingOverview, setLoadingOverview] = useState(true);
  const [loadingDimension, setLoadingDimension] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Bumped on manual refresh so any already-expanded detail rows refetch.
  const [detailRefreshToken, setDetailRefreshToken] = useState(0);

  // Name search for a specific entity of the selected dimension. When a query
  // is active the table shows the matched entities (which may include ones with
  // no milestones in scope) instead of the full breakdown.
  const [searchInput, setSearchInput] = useState<string>('');
  const [searchResults, setSearchResults] = useState<EntitySearchResult[] | null>(
    null,
  );
  const [searchLoading, setSearchLoading] = useState(false);

  // Which coverage card is drilled into: 'with' | 'without' | null.
  const [coverageOpen, setCoverageOpen] = useState<'with' | 'without' | null>(
    null,
  );

  // Company product/therapeutic breakdown mode (Company dimension only).
  const [byProduct, setByProduct] = useState(false);
  const [productCompany, setProductCompany] = useState<EntitySearchResult | null>(
    null,
  );
  const [companyProducts, setCompanyProducts] = useState<
    MilestoneDimensionRow[] | null
  >(null);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productsError, setProductsError] = useState(false);

  const fetchOverview = useCallback(async () => {
    setLoadingOverview(true);
    try {
      // days scopes the presentation coverage; status + age cards ignore it.
      const data = await getMilestoneOverview(appliedDays);
      setOverview(data);
    } catch (e) {
      setError('Failed to load milestone overview.');
    } finally {
      setLoadingOverview(false);
    }
    // `environment` is a dependency: the axios client reads the active
    // environment from sessionStorage at request time, so a change must refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [environment, appliedDays]);

  const fetchDimension = useCallback(async () => {
    setLoadingDimension(true);
    try {
      const data = await getMilestoneStatsByDimension(dimension, appliedDays);
      setDimensionStats(data);
    } catch (e) {
      setError('Failed to load milestone breakdown.');
    } finally {
      setLoadingDimension(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dimension, appliedDays, environment]);

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  useEffect(() => {
    fetchDimension();
  }, [fetchDimension]);

  // Debounce the days input into appliedDays.
  useEffect(() => {
    const handle = setTimeout(() => {
      const trimmed = daysInput.trim();
      if (trimmed === '') {
        setAppliedDays(undefined);
        return;
      }
      const parsed = parseInt(trimmed, 10);
      setAppliedDays(Number.isFinite(parsed) && parsed > 0 ? parsed : undefined);
    }, 400);
    return () => clearTimeout(handle);
  }, [daysInput]);

  // Debounced entity name search against the selected dimension. A blank query
  // clears the search (falls back to the full breakdown). Stale responses are
  // ignored via the `active` flag.
  useEffect(() => {
    const trimmed = searchInput.trim();
    if (trimmed === '') {
      setSearchResults(null);
      setSearchLoading(false);
      return;
    }
    let active = true;
    setSearchLoading(true);
    const handle = setTimeout(async () => {
      try {
        const results = await searchEntities(dimension, trimmed);
        if (active) setSearchResults(results);
      } catch (e) {
        if (active) setSearchResults([]);
      } finally {
        if (active) setSearchLoading(false);
      }
    }, 400);
    return () => {
      active = false;
      clearTimeout(handle);
    };
  }, [searchInput, dimension, environment]);

  // Clear an active search whenever the dimension changes so results never
  // belong to a different entity type than the one selected.
  useEffect(() => {
    setSearchInput('');
  }, [dimension]);

  // The product breakdown only applies to the Company dimension. Leaving Company
  // turns it off and clears any picked company.
  useEffect(() => {
    if (dimension !== 'company') {
      setByProduct(false);
      setProductCompany(null);
      setCompanyProducts(null);
    }
  }, [dimension]);

  // Fetch the selected company's therapeutics when in product mode.
  useEffect(() => {
    if (!byProduct || !productCompany) {
      setCompanyProducts(null);
      return;
    }
    let active = true;
    setProductsLoading(true);
    setProductsError(false);
    (async () => {
      try {
        const data = await getCompanyProducts(productCompany.id);
        if (active) setCompanyProducts(data.products);
      } catch (e) {
        if (active) setProductsError(true);
      } finally {
        if (active) setProductsLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [byProduct, productCompany, environment, detailRefreshToken]);

  const refresh = () => {
    setError(null);
    setDetailRefreshToken((t) => t + 1);
    fetchOverview();
    fetchDimension();
  };

  const ageBuckets = overview?.active_age_buckets;
  const statusCounts = overview?.status_counts;
  const coverage = overview?.presentation_coverage;

  // Rows to render: search results (merged with breakdown counts where known)
  // when a search is active, otherwise the full breakdown.
  const searchActive = searchInput.trim() !== '';
  const displayedRows: MilestoneDimensionRow[] = useMemo(() => {
    const breakdownRows = dimensionStats?.rows ?? [];
    if (!searchActive) return breakdownRows;
    if (!searchResults) return [];
    const byId = new Map(breakdownRows.map((r) => [r.id, r]));
    return searchResults.map((s) => {
      const existing = byId.get(s.id);
      if (existing) return existing;
      // Matched entity not in the (days-scoped) breakdown: show zero counts.
      // Its expansion will fetch milestones (possibly none) on demand.
      return {
        id: s.id,
        name: s.name,
        active: 0,
        completed: 0,
        canceled: 0,
        total: 0,
      };
    });
  }, [searchActive, searchResults, dimensionStats]);

  const coveragePct = useMemo(() => {
    if (!coverage || coverage.total === 0) return 0;
    return Math.round((coverage.with_milestones / coverage.total) * 100);
  }, [coverage]);

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <FlagIcon color="primary" />
        <Typography variant="h5" sx={{ fontWeight: 700, flex: 1 }}>
          Milestone Dashboard
        </Typography>
        <Tooltip title="Refresh">
          <IconButton onClick={refresh} aria-label="refresh">
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {error && <ErrorMessage message={error} onDismiss={() => setError(null)} />}

      {/* --- Status + Active-by-age stat cards --- */}
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
        Active milestones by age (since recorded)
      </Typography>
      {/* Only show the full spinner on the very first load. On later refetches
          (e.g. changing the days filter) we keep the section mounted so the
          filter input never disappears mid-edit. */}
      {!ageBuckets || !statusCounts ? (
        <LoadingSpinner message="Loading milestone stats..." />
      ) : (
        <>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
            <StatCard
              label="≤ 30 days"
              value={ageBuckets.within_30}
              icon={<FlagIcon />}
              color="#2e7d32"
            />
            <StatCard
              label="31–60 days"
              value={ageBuckets.within_60}
              icon={<FlagIcon />}
              color="#558b2f"
            />
            <StatCard
              label="61–90 days"
              value={ageBuckets.within_90}
              icon={<FlagIcon />}
              color="#f9a825"
            />
            <StatCard
              label="91–365 days"
              value={ageBuckets.within_365}
              icon={<FlagIcon />}
              color="#ef6c00"
            />
            <StatCard
              label="> 1 year"
              value={ageBuckets.over_365}
              icon={<FlagIcon />}
              color="#c62828"
              hint="Active milestones recorded more than a year ago"
            />
            <StatCard
              label="Total Active"
              value={statusCounts.active}
              icon={<CheckCircleIcon />}
              color="#1565c0"
            />
            <StatCard
              label="Cancelled"
              value={statusCounts.canceled}
              icon={<CancelIcon />}
              color="#757575"
            />
            <StatCard
              label="Completed"
              value={statusCounts.completed}
              icon={<CheckCircleIcon />}
              color="#455a64"
            />
          </Box>

          {/* --- Shared days filter: scopes coverage + breakdown below --- */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              mb: 3,
              p: 1.5,
              borderRadius: 1,
              bgcolor: 'action.hover',
              flexWrap: 'wrap',
            }}
          >
            <TextField
              size="small"
              type="number"
              label="Within N days"
              value={daysInput}
              onChange={(e) => setDaysInput(e.target.value)}
              placeholder="All time"
              inputProps={{ min: 1 }}
              sx={{ width: 160, bgcolor: 'background.paper' }}
            />
            <Typography variant="body2" color="text.secondary">
              Filters presentation coverage and the breakdown below by milestone
              age (since recorded). Leave blank for all time.
            </Typography>
          </Box>

          {/* --- Presentation coverage (dims briefly while the filter refetches) --- */}
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Presentation coverage
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 2,
              mb: 4,
              opacity: loadingOverview ? 0.5 : 1,
              transition: 'opacity 0.2s',
            }}
          >
            <StatCard
              label="With milestones"
              value={coverage?.with_milestones ?? 0}
              icon={<SlideshowIcon />}
              color="#2e7d32"
              hint={`${coveragePct}% of ${(coverage?.total ?? 0).toLocaleString()} presentations`}
              selected={coverageOpen === 'with'}
              onClick={() =>
                setCoverageOpen((cur) => (cur === 'with' ? null : 'with'))
              }
            />
            <StatCard
              label="Without milestones"
              value={coverage?.without_milestones ?? 0}
              icon={<SlideshowIcon />}
              color="#c62828"
              selected={coverageOpen === 'without'}
              onClick={() =>
                setCoverageOpen((cur) => (cur === 'without' ? null : 'without'))
              }
            />
            <StatCard
              label="Total presentations"
              value={coverage?.total ?? 0}
              icon={<SlideshowIcon />}
              color="#1565c0"
            />
          </Box>

          {/* --- Coverage drill-down: paginated presentation list --- */}
          <Collapse in={coverageOpen !== null} timeout="auto" unmountOnExit>
            {coverageOpen !== null && (
              <CoverageDrilldown
                hasMilestones={coverageOpen === 'with'}
                days={appliedDays}
                refreshToken={detailRefreshToken}
              />
            )}
          </Collapse>
        </>
      )}

      {/* --- Breakdown by dimension --- */}
      <Box sx={{ mb: 2 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Typography variant="subtitle2" color="text.secondary">
            Milestones by
          </Typography>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={dimension}
            onChange={(_, value) => value && setDimension(value)}
          >
            <ToggleButton value="company">
              <BusinessIcon fontSize="small" sx={{ mr: 0.5 }} /> Company
            </ToggleButton>
            <ToggleButton value="technology">
              <ScienceIcon fontSize="small" sx={{ mr: 0.5 }} /> Technology
            </ToggleButton>
            <ToggleButton value="disease">
              <DiseaseIcon fontSize="small" sx={{ mr: 0.5 }} /> Disease
            </ToggleButton>
          </ToggleButtonGroup>

          {/* Product/therapeutic breakdown — Company dimension only. */}
          {dimension === 'company' && (
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={byProduct}
                  onChange={(e) => {
                    setByProduct(e.target.checked);
                    setSearchInput('');
                    setProductCompany(null);
                  }}
                />
              }
              label="Break down by product/therapeutic"
            />
          )}
        </Box>

        {/* Search box: filters the breakdown, or picks a company in product mode. */}
        <TextField
          size="small"
          fullWidth
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder={
            byProduct
              ? 'Search a company to view its therapeutics…'
              : `Search ${DIMENSION_LABELS[dimension].toLowerCase()} by name…`
          }
          sx={{ mt: 1.5, maxWidth: 480 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: searchInput ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  aria-label="clear search"
                  onClick={() => setSearchInput('')}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />

        {/* In product mode, show company search results to pick from. */}
        {byProduct && searchInput.trim() !== '' && !productCompany && (
          <Paper variant="outlined" sx={{ mt: 1, maxWidth: 480 }}>
            {searchLoading ? (
              <Box sx={{ p: 2 }}>
                <LoadingSpinner message="Searching companies…" />
              </Box>
            ) : !searchResults || searchResults.length === 0 ? (
              <Box sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  No companies match “{searchInput.trim()}”.
                </Typography>
              </Box>
            ) : (
              <List dense disablePadding sx={{ maxHeight: 260, overflow: 'auto' }}>
                {searchResults.map((c) => (
                  <ListItemButton
                    key={c.id}
                    onClick={() => {
                      setProductCompany(c);
                      setSearchInput('');
                    }}
                  >
                    <ListItemText primary={c.name || c.id} />
                  </ListItemButton>
                ))}
              </List>
            )}
          </Paper>
        )}

        {/* Selected company chip in product mode. */}
        {byProduct && productCompany && (
          <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Therapeutics for
            </Typography>
            <Chip
              label={productCompany.name || productCompany.id}
              onDelete={() => setProductCompany(null)}
              color="primary"
              variant="outlined"
            />
          </Box>
        )}
      </Box>

      {byProduct ? (
        // --- Company product/therapeutic breakdown ---
        productCompany ? (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 48 }} />
                  <TableCell>Therapeutic</TableCell>
                  <TableCell align="right">Active</TableCell>
                  <TableCell align="right">Completed</TableCell>
                  <TableCell align="right">Cancelled</TableCell>
                  <TableCell align="right">Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {productsLoading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <LoadingSpinner message="Loading therapeutics..." />
                    </TableCell>
                  </TableRow>
                ) : productsError ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="error">
                        Failed to load therapeutics.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : !companyProducts || companyProducts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        No therapeutics found for this company.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  companyProducts.map((row) => (
                    <MilestoneBreakdownRow
                      key={`product-${row.id}`}
                      row={row}
                      dimension="product"
                      days={appliedDays}
                      refreshToken={detailRefreshToken}
                    />
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Typography variant="body2" color="text.secondary">
              Search and select a company above to view all of its therapeutics
              and their milestones.
            </Typography>
          </Paper>
        )
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 48 }} />
                <TableCell>{DIMENSION_LABELS[dimension]}</TableCell>
                <TableCell align="right">Active</TableCell>
                <TableCell align="right">Completed</TableCell>
                <TableCell align="right">Cancelled</TableCell>
                <TableCell align="right">Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(searchActive ? searchLoading : loadingDimension) ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <LoadingSpinner
                      message={
                        searchActive ? 'Searching…' : 'Loading breakdown...'
                      }
                    />
                  </TableCell>
                </TableRow>
              ) : displayedRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      {searchActive
                        ? `No ${DIMENSION_LABELS[
                            dimension
                          ].toLowerCase()} matches “${searchInput.trim()}”.`
                        : `No milestones found${
                            appliedDays ? ` in the last ${appliedDays} days` : ''
                          }.`}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                displayedRows.map((row) => (
                  <MilestoneBreakdownRow
                    key={`${dimension}-${row.id}`}
                    row={row}
                    dimension={dimension}
                    days={appliedDays}
                    refreshToken={detailRefreshToken}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Container>
  );
};

export default MilestoneDashboardPage;
